import { SearchRegistry } from '../types';

export const accountManagementSearchEntries: SearchRegistry = {
  '/account-management/payment-method': {
    keywords: [
      'pay by invoice',
      'payment method',
      'invoice payment',
      'deposit',
      'security deposit',
      'spending limit',
      'billing cycle',
      'pay by invoice setup',
      'switch to invoice',
      'invoice onboarding',
      'deposit calculator',
      'company data',
      'terms and conditions',
      'bank transfer',
      'iban',
      'sepa transfer',
      'transfer declaration',
      'activate pay by invoice',
      'invoice activation',
      'payment upgrade',
      'credit card to invoice',
    ],
    questions: [
      'How do I switch to Pay by Invoice?',
      'What is the deposit for Pay by Invoice?',
      'How is the deposit amount calculated?',
      'What billing cycle should I choose?',
      'How do I declare my bank transfer?',
      'When will my Pay by Invoice be activated?',
      'What is my spending limit?',
      'Can I change my billing cycle?',
      'How do I forward transfer details to my finance team?',
    ],
    faqs: [
      {
        q: 'How do I switch to Pay by Invoice?',
        a: 'Go to Administration → Payment Method and click "Switch to Pay by Invoice". You will go through a short wizard: set your monthly spend to calculate a security deposit, enter your company details, then make the deposit transfer. ryd Finance verifies the transfer and activates the payment method within 1–2 business days.',
      },
      {
        q: 'How is the deposit amount calculated?',
        a: 'The deposit equals your expected monthly spend divided by the number of billing periods per month, rounded up to the next €50 (minimum €100). A 10-day billing cycle gives the lowest deposit (÷3), a 7-day cycle is mid-range (÷4.3), and a 2-day cycle gives the highest (÷15).',
      },
      {
        q: 'What billing cycle should I choose?',
        a: 'The 10-day cycle (3 invoices per month) gives the lowest security deposit and is recommended for most fleets. Choose a shorter cycle only if cash flow allows — the 2-day cycle requires significantly more administrative overhead.',
      },
      {
        q: 'How do I declare my bank transfer?',
        a: 'In the Transfer Declaration step, copy the exact IBAN, BIC, amount, and payment reference shown. Make the transfer from your company bank account, then return to confirm it and enter when you initiated it. You can also forward the bank details to your finance team directly from the wizard.',
      },
      {
        q: 'How long does activation take?',
        a: 'ryd Finance verifies your transfer within 1–2 business days. You will receive an email confirmation once Pay by Invoice is active.',
      },
    ],
  },
  '/account-management/invoices': {
    keywords: [
      'invoices',
      'invoice management',
      'fleet invoices',
      'billing',
      'rechnungen',
      'invoice download',
      'invoice pdf',
      'monthly invoice',
      'billing cycle',
      'invoice number filter',
      'manage cc recipients',
      'cc recipients',
      'export invoices',
      'time frame filter',
      'invoice attachment',
      'sort invoices',
      'invoice table',
    ],
    questions: [
      'Where do I find my fleet invoices?',
      'How do I download an invoice PDF?',
      'When is the next invoice generated?',
      'How do I see past invoices for a specific month?',
      'How do I add CC recipients to my invoice emails?',
    ],
    faqs: [
      {
        q: 'Where do I download my fleet invoices?',
        a: 'Open Administration → Invoices. Click an invoice row to download the PDF attachment. Invoices are typically generated at the end of the configured billing cycle.',
      },
      {
        q: 'How do I add additional email recipients (CC) to invoice notifications?',
        a: 'On the Invoices page, click the Manage CC Recipients button next to the export action. Each address you add receives the same invoice email when a new invoice is generated.',
      },
      {
        q: 'How do I find a specific invoice by number?',
        a: 'Use the Invoice Number filter at the top of the Invoices page — it offers a searchable picker of available invoice numbers, scoped to the chosen time frame. Combine it with the time-frame filter to pinpoint the right document.',
      },
    ],
  },
  '/account-management/list-prices': {
    keywords: [
      'list prices',
      'list price',
      'fuel prices',
      'price list',
      'partner prices',
      'wholesale prices',
      'station prices',
      'preisliste',
      'kraftstoffpreise',
      'effective date',
      'price valid from',
      'list price page',
    ],
    questions: [
      'Where do I see my current list prices?',
      'How do I check the prices for my fuel stations?',
      'Where can I download the price list?',
      'When does a new list price become effective?',
    ],
    faqs: [
      {
        q: 'Where do I see the list prices for my partner account?',
        a: 'Open Administration → List Prices. The page shows the current prices configured for your partner along with the effective date.',
      },
    ],
  },
  '/account-management/account-settings': {
    keywords: [
      'account settings',
      'partner settings',
      'profile settings',
      'preferences',
      'account configuration',
      'company settings',
      'einstellungen',
      'notification preferences',
      'invoice email',
      'company contact',
      'account info',
    ],
    questions: [
      'Where do I edit my account settings?',
      'How do I update my partner profile?',
      'Where can I change my notification preferences?',
      'How do I change my invoice email?',
    ],
  },
  '/account-management/account-settings-poland-invoice': {
    keywords: [
      'account settings',
      'poland invoice',
      'exorigo',
      'exorigo upos',
      'upos',
      'partner settings',
      'invoice settings',
      'poland integration settings',
    ],
    questions: [
      'Where are the Exorigo/Upos partner account settings?',
      'How do I configure the Poland invoice partner integration?',
    ],
  },
  '/account-management/account-settings-individual': {
    keywords: [
      'account settings',
      'oilpay',
      'allguth',
      'partner settings',
      'individual partner settings',
      'preferences',
      'oilpay settings',
      'allguth settings',
    ],
    questions: ['Where do I edit my Oilpay/Allguth account settings?'],
  },
  '/account-management/exports': {
    keywords: [
      'exports',
      'export centre',
      'export center',
      'download centre',
      'download center',
      'download files',
      'async exports',
      'asynchronous exports',
      'export jobs',
      'recent exports',
      'partner exports',
      'generated files',
      'csv exports',
      'file format',
      'export status',
      'download csv',
      'partner reports',
      'transaction list export',
      'order list csv',
      'staging exports',
      'production exports',
      'environment selector',
    ],
    questions: [
      'Where do I download generated export files?',
      'Where can I see async export jobs?',
      'How do I download a completed CSV export?',
      'Where do partner reports and transaction exports appear?',
      'How do I see the file format of an export?',
    ],
    faqs: [
      {
        q: 'Where do completed exports appear?',
        a: 'Open Administration → Exports. The page lists recent asynchronous export jobs for the selected environment, including status, file format and the download action when a file is ready.',
      },
      {
        q: 'How do I download a transaction CSV after starting an export?',
        a: 'Open Administration → Exports, choose the same staging or production environment, and use Download on the completed CSV job. Pending and failed jobs remain visible with their current status.',
      },
    ],
  },
};
